import React, { useContext } from "react";
import { Box, Container, Grid, Typography, Card, CardContent, Avatar } from "@mui/material";
import Header from "components/Header/Header.js";
import HomeFooter from "components/Footer/HomeFooter.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import { useTranslation } from "react-i18next";
import { CheckCircle, Users, Target, Zap } from "lucide-react";
import { ThemeContext } from "components/Theme/ThemeContext";

export default function AboutUs(props) {
  const { t } = useTranslation();
  const { isDark, currentTheme } = useContext(ThemeContext);
  const { ...rest } = props;

  return (
    <Box sx={{ bgcolor: currentTheme.background, display: 'flex', flexDirection: 'column', transition: 'background-color 0.3s ease, color 0.3s ease' }}>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Header
          color="transparent"
          routes={[]}
          rightLinks={<HeaderLinks />}
          fixed
          changeColorOnScroll={{
            height: 100,
            color: isDark ? "dark" : "white"
          }}
          {...rest}
        />

        {/* Hero Section */}
        <Box sx={{
          background: isDark ? 'linear-gradient(135deg, #0A0A0B 0%, #161619 100%)' : 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
          color: isDark ? 'white' : '#0F172A',
          pt: 20,
          pb: 12,
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease'
        }}>
          <Box sx={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: '300px',
            height: '300px',
            background: isDark ? 'radial-gradient(circle, rgba(233, 174, 49, 0.15) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(233, 174, 49, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(50px)',
          }} />
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <Typography variant="h2" sx={{ fontWeight: 800, mb: 3, fontFamily: 'Outfit, sans-serif', color: isDark ? 'white' : '#0F172A' }}>
              {t('about_us')}
            </Typography>
            <Typography variant="h5" sx={{ opacity: 0.8, maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: isDark ? '#94a3b8' : '#475569' }}>
              {t('about_us_tagline')}
            </Typography>
          </Container>
        </Box>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ py: 8, flex: 1 }}>
          <Grid container spacing={6}>
            {/* Intro Section */}
            <Grid item xs={12}>
              <Card sx={{
                border: 'none',
                background: isDark ? 'rgba(30, 41, 59, 0.4)' : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(12px)',
                borderRadius: '20px',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(233, 174, 49, 0.15)',
                boxShadow: isDark ? '0 8px 32px 0 rgba(0, 0, 0, 0.2)' : '0 8px 32px 0 rgba(233, 174, 49, 0.03)',
                overflow: 'hidden'
              }}>
                <CardContent sx={{ p: 6 }}>
                  <Typography variant="body1" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '1.1rem', lineHeight: 1.8 }}>
                    {t('about_us_content3')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* What We Do */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                p: 4,
                height: '100%',
                background: isDark ? 'rgba(30, 41, 59, 0.4)' : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(12px)',
                borderRadius: '20px',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(233, 174, 49, 0.1)',
                boxShadow: isDark ? '0 8px 32px 0 rgba(0, 0, 0, 0.2)' : '0 8px 32px 0 rgba(233, 174, 49, 0.03)',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)', boxShadow: isDark ? '0 12px 40px rgba(0, 0, 0, 0.4)' : '0 12px 40px rgba(233, 174, 49, 0.08)' }
              }}>
                <Avatar sx={{ bgcolor: isDark ? 'rgba(233, 174, 49, 0.1)' : '#eff6ff', color: '#E9AE31', width: 56, height: 56, mb: 3 }}>
                  <Zap size={28} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, fontFamily: 'Outfit, sans-serif', color: isDark ? 'white' : '#0F172A' }}>
                  {t('what_we_do')}
                </Typography>
                <Typography variant="body1" sx={{ color: isDark ? '#94a3b8' : '#64748b', lineHeight: 1.7 }}>
                  {t('what_we_do_content')}
                </Typography>
              </Box>
            </Grid>

            {/* What Sets Us Apart */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                p: 4,
                height: '100%',
                background: isDark ? 'rgba(30, 41, 59, 0.4)' : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(12px)',
                borderRadius: '20px',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(233, 174, 49, 0.1)',
                boxShadow: isDark ? '0 8px 32px 0 rgba(0, 0, 0, 0.2)' : '0 8px 32px 0 rgba(233, 174, 49, 0.03)',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)', boxShadow: isDark ? '0 12px 40px rgba(0, 0, 0, 0.4)' : '0 12px 40px rgba(233, 174, 49, 0.08)' }
              }}>
                <Avatar sx={{ bgcolor: isDark ? 'rgba(22, 163, 74, 0.1)' : '#f0fdf4', color: '#16a34a', width: 56, height: 56, mb: 3 }}>
                  <CheckCircle size={28} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, fontFamily: 'Outfit, sans-serif', color: isDark ? 'white' : '#0F172A' }}>
                  {t('what_sets_us_apart')}
                </Typography>
                <Typography variant="body1" sx={{ color: isDark ? '#94a3b8' : '#64748b', lineHeight: 1.7 }}>
                  {t('what_sets_us_apart_content')}
                </Typography>
              </Box>
            </Grid>

            {/* Our Team */}
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Avatar sx={{ bgcolor: isDark ? 'rgba(192, 38, 211, 0.1)' : '#fdf4ff', color: '#c026d3', width: 64, height: 64, mx: 'auto', mb: 3 }}>
                  <Users size={32} />
                </Avatar>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 3, fontFamily: 'Outfit, sans-serif', color: isDark ? 'white' : '#0F172A' }}>
                  {t('our_team')}
                </Typography>
                <Typography variant="body1" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '1.1rem', maxWidth: '800px', mx: 'auto', lineHeight: 1.8 }}>
                  {t('our_team_content')}
                </Typography>
              </Box>
            </Grid>

            {/* Get In Touch */}
            <Grid item xs={12}>
              <Box sx={{
                textAlign: 'center',
                mt: 8,
                p: 6,
                background: isDark ? 'rgba(30, 41, 59, 0.4)' : '#fff',
                backdropFilter: 'blur(12px)',
                borderRadius: '20px',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(233, 174, 49, 0.1)',
                boxShadow: isDark ? '0 8px 32px 0 rgba(0, 0, 0, 0.2)' : '0 4px 20px rgba(0,0,0,0.05)',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)', boxShadow: isDark ? '0 12px 40px rgba(0, 0, 0, 0.4)' : '0 12px 40px rgba(233, 174, 49, 0.08)' }
              }}>
                <Avatar sx={{ bgcolor: isDark ? 'rgba(234, 88, 12, 0.1)' : '#fff7ed', color: '#ea580c', width: 64, height: 64, mx: 'auto', mb: 3 }}>
                  <Target size={32} />
                </Avatar>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 3, fontFamily: 'Outfit, sans-serif', color: isDark ? 'white' : '#0F172A' }}>
                  {t('get_in_touch')}
                </Typography>
                <Typography variant="body1" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '1.1rem', maxWidth: '800px', mx: 'auto', lineHeight: 1.8 }}>
                  {t('get_in_touch_content')}
                </Typography>
              </Box>
            </Grid>

          </Grid>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
}
